import { useAccount } from '@azure/msal-react';
import snakeCase from 'lodash/snakeCase';
import { useMutation, useQueryClient } from 'react-query';
import { useApiPost, useApiQuery } from 'src/api';
import { useBuildVars } from 'src/config';
import { getReactQueryOptions } from 'src/views/customerGroups/hooks/customerGroupMutations';
import { useQueryParams } from 'src/views/customerGroups/hooks/hooks';

export const ProductGroupSearchFilters = ['name', 'id'] as const;
export type ProductGroupSearchFilter = typeof ProductGroupSearchFilters[number];
export interface ProductGroup {
  created: string;
  description: string;
  id: number;
  lastupdated: string;
  name: string;
  productcount: number;
  status: boolean;
}
export interface ResponsePreamble {
  currentPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}
export interface ProductGroupsResponse {
  data: ProductGroup[];
  responsePreamble: ResponsePreamble;
}

export function useProductGroupQueryParams({
  filter,
  pageSize: initialPageSize,
}: any = {}) {
  const {
    searchQuery: { sort, ...searchQuery },
    ...rest
  } = useQueryParams<ProductGroupSearchFilter, keyof ProductGroup>({
    filter: filter ?? {
      field: 'name' as const,
      value: '',
    },
    pageSize: initialPageSize,
    sort: {
      field: 'name' as const,
      sort: 'asc' as const,
    },
  });

  return {
    searchQuery: {
      ...searchQuery,
      sort: `${rest.sort?.sort?.sort === 'desc' ? '-' : ''}${snakeCase(
        rest.sort?.sort?.field
      )}`,
    },
    ...rest,
  };
}
export function useGetProductGroups({ ...query }: any, enabled?: boolean) {
  const buildVars = useBuildVars();
  return useApiQuery<ProductGroupsResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductGroups',
    query,
    { enabled: enabled ?? true },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useProductGroupCreate() {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const apiPost = useApiPost({
    host: buildVars.VP_API_BASE,
  });
  return useMutation(
    ({ description, name }: any) =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/CreateProductGroups',
        {
          createdBy: userName,
          description,
          name,
        }
      ),
    getReactQueryOptions(queryClient, [
      '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductGroups',
    ])
  );
}
